import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const SLIDES = [
  {
    gradient: 'from-[#8B5CF6] via-[#F472B6] to-[#FCD34D]',
    badge: { emoji: '🎉', text: 'Summer Sale - Up to 50% OFF', from: 'from-yellow-300', to: 'to-pink-400' },
    headline: ['Where Dreams', 'Come to '],
    highlight: 'Play!',
    highlightGrad: 'from-yellow-300 via-pink-300 to-yellow-300',
    waveSvgColor: '#FCD34D',
    body: 'Discover magical toys, super cool ride-ons, and amazing learning games for endless fun! 🚀',
    primaryBtn: { label: 'Shop Toys', grad: 'from-yellow-300 via-yellow-400 to-yellow-300' },
    secondaryBtn: { label: 'Explore Products', textColor: 'text-primary' },
    centerEmoji: '🧸',
    floatingEmojis: [
      { emoji: '🧸', pos: 'absolute -top-8 -left-8', size: 'w-28 h-28', grad: 'from-pink-400 to-pink-600', delay: '0s' },
      { emoji: '🚗', pos: 'absolute -bottom-8 -right-8', size: 'w-32 h-32', grad: 'from-blue-400 to-blue-600', delay: '1s' },
      { emoji: '🚲', pos: 'absolute top-1/3 -right-12', size: 'w-24 h-24', grad: 'from-green-400 to-green-600', delay: '0.5s' },
      { emoji: '🎨', pos: 'absolute bottom-1/3 -left-12', size: 'w-20 h-20', grad: 'from-yellow-400 to-yellow-600', delay: '1.5s' },
    ],
  },
  {
    gradient: 'from-[#60A5FA] via-[#34D399] to-[#FCD34D]',
    badge: { emoji: '🚗', text: 'New Electric Cars Collection', from: 'from-green-300', to: 'to-blue-400' },
    headline: ['Ride Into', ''],
    highlight: 'Adventure!',
    highlightGrad: 'from-green-300 via-yellow-300 to-green-300',
    waveSvgColor: '#34D399',
    body: 'Premium electric ride-on cars with realistic features and ultimate safety! 🏎️',
    primaryBtn: { label: 'Shop Cars', grad: 'from-green-300 via-green-400 to-green-300' },
    secondaryBtn: { label: 'View Collection', textColor: 'text-accent3' },
    centerEmoji: '🏎️',
    floatingEmojis: [
      { emoji: '🏎️', pos: 'absolute -top-8 -left-8', size: 'w-28 h-28', grad: 'from-blue-400 to-blue-600', delay: '0s' },
      { emoji: '🚙', pos: 'absolute -bottom-8 -right-8', size: 'w-32 h-32', grad: 'from-green-400 to-green-600', delay: '1s' },
    ],
  },
  {
    gradient: 'from-[#F472B6] via-[#8B5CF6] to-[#FCD34D]',
    badge: { emoji: '🧩', text: 'Educational Toys & Games', from: 'from-purple-300', to: 'to-pink-400' },
    headline: ['Learn While', 'You '],
    highlight: 'Play!',
    highlightGrad: 'from-purple-300 via-pink-300 to-purple-300',
    waveSvgColor: '#F472B6',
    body: 'Boost creativity and learning with fun educational toys and puzzles! 🎓',
    primaryBtn: { label: 'Explore Now', grad: 'from-purple-300 via-purple-400 to-purple-300' },
    secondaryBtn: { label: 'See All Toys', textColor: 'text-primary' },
    centerEmoji: '🧩',
    floatingEmojis: [
      { emoji: '🧩', pos: 'absolute -top-8 -left-8', size: 'w-28 h-28', grad: 'from-purple-400 to-purple-600', delay: '0s' },
      { emoji: '📚', pos: 'absolute -bottom-8 -right-8', size: 'w-32 h-32', grad: 'from-pink-400 to-pink-600', delay: '1s' },
    ],
  },
];

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((c) => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  const slide = SLIDES[current];

  return (
    <section className="relative overflow-hidden min-h-[600px] lg:min-h-[680px]">
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} transition-all duration-700`} />

      <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-40 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex items-center min-h-[600px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">

          <div className="text-center lg:text-left">
            <div className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${slide.badge.from} ${slide.badge.to} rounded-full text-textDark font-black text-sm mb-6 shadow-2xl`}>
              <span className="text-2xl">{slide.badge.emoji}</span>
              <span>{slide.badge.text}</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
              {slide.headline[0]}<br />
              {slide.headline[1]}
              <span className="relative inline-block">
                <span className={`relative z-10 bg-gradient-to-r ${slide.highlightGrad} bg-clip-text text-transparent`}>
                  {slide.highlight}
                </span>
                <svg className="absolute w-full h-6 -bottom-2 left-0 z-0" viewBox="0 0 200 20">
                  <path d="M0,10 Q25,5 50,10 T100,10 T150,10 T200,10" stroke={slide.waveSvgColor} strokeWidth="8" fill="none" strokeLinecap="round" opacity="0.8" />
                </svg>
              </span>
            </h1>

            <p className="text-xl text-white/95 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0 font-bold"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
              {slide.body}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button className={`group bg-gradient-to-r ${slide.primaryBtn.grad} text-textDark font-black text-lg px-10 py-5 rounded-full shadow-2xl transform hover:-translate-y-2 transition-all flex items-center gap-2 justify-center`}>
                {slide.primaryBtn.label}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className={`bg-white ${slide.secondaryBtn.textColor} font-bold text-lg px-10 py-5 rounded-full border-4 border-white/50 transition-all hover:border-white hover:shadow-2xl transform hover:-translate-y-1`}>
                {slide.secondaryBtn.label}
              </button>
            </div>
          </div>

          <div className="relative h-80 lg:h-full flex items-center justify-center">
            <div className="relative w-full max-w-lg py-16">
              <div className="absolute inset-0 bg-white/20 rounded-full blur-3xl" />
              <div className="relative z-10 text-center text-[150px] leading-none select-none">
                {slide.centerEmoji}
              </div>
              {slide.floatingEmojis.map((fe, i) => (
                <div
                  key={i}
                  className={`${fe.pos} ${fe.size} bg-gradient-to-br ${fe.grad} rounded-3xl shadow-2xl flex items-center justify-center border-4 border-white`}
                  style={{ animation: `bounce ${3.5 + i * 0.5}s ease-in-out infinite`, animationDelay: fe.delay }}
                >
                  <span className="text-4xl">{fe.emoji}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-4 z-30">
        <button onClick={prev} className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-lg border-2 border-white/60 flex items-center justify-center text-white hover:bg-white/40 transition-all shadow-xl">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-3 bg-white/20 backdrop-blur-lg px-4 py-3 rounded-full border-2 border-white/40">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-3 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-white' : 'w-3 bg-white/40 hover:bg-white/60'}`}
            />
          ))}
        </div>
        <button onClick={next} className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-lg border-2 border-white/60 flex items-center justify-center text-white hover:bg-white/40 transition-all shadow-xl">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="absolute bottom-0 left-0 w-full z-20 pointer-events-none">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-20">
          <defs>
            <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity="1" />
              <stop offset="50%" stopColor="#F472B6" stopOpacity="1" />
              <stop offset="100%" stopColor="#60A5FA" stopOpacity="1" />
            </linearGradient>
          </defs>
          <path d="M0,40 Q120,10 240,40 T480,40 T720,40 T960,40 T1200,40 T1440,40 L1440,120 L0,120 Z" fill="url(#waveGrad)" opacity="0.3" />
          <path d="M0,60 Q180,30 360,60 T720,60 T1080,60 T1440,60 L1440,120 L0,120 Z" fill="url(#waveGrad)" opacity="0.5" />
          <path d="M0,80 Q240,50 480,80 T960,80 T1440,80 L1440,120 L0,120 Z" fill="#ffffff" />
        </svg>
      </div>
    </section>
  );
}
